import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Tag, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import {
  SalesOrderLine,
  SalesOrderLineAuditEntry,
} from '@totem/components/salesOrders/list/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DateStringToLocal } from '@totem/utilities/dateUtilities';
import { SALES_ORDERS_ENDPOINT } from '@totem/utilities/endpoints';

const { Text } = Typography;

interface HistoryRow {
  key: string;
  timestamp: string;
  source: string;
  changedByName: string;
  field: string;
  before: string;
  after: string;
}

const getSourceColor = (source: string): string => {
  switch (source) {
    case 'NetSuite Sync':
      return 'blue';
    case 'User':
      return 'green';
    default:
      return 'default';
  }
};

const renderValue = (value: string) =>
  value !== '' ? <span>{value}</span> : <Text type="secondary">—</Text>;

export interface Props {
  salesOrderLine: SalesOrderLine;
  onClose: () => void;
}

const SalesOrderLineHistoryModal = ({ salesOrderLine, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [audit, setAudit] = useState<SalesOrderLineAuditEntry[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    setLoading(true);

    fetch(`${SALES_ORDERS_ENDPOINT}/${salesOrderLine.id}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: SalesOrderLine) => setAudit(result?.audit ?? []))
      .then(() => setLoading(false));
  }, [salesOrderLine.id, getApiToken]);

  const rows = useMemo<HistoryRow[]>(
    () =>
      [...audit]
        .sort((entryA, entryB) =>
          entryB.timestamp.localeCompare(entryA.timestamp),
        )
        .flatMap((entry) =>
          (entry.changes ?? []).map((change, idx) => ({
            key: `${entry.id}-${idx}`,
            timestamp: entry.timestamp,
            source: entry.source,
            changedByName: entry.changedByName,
            field: change.field,
            before: change.before,
            after: change.after,
          })),
        ),
    [audit],
  );

  const columns: ColumnProps<HistoryRow>[] = [
    {
      title: 'Date',
      key: 'timestamp',
      render: (_, record: HistoryRow) => (
        <span>{DateStringToLocal(record.timestamp)}</span>
      ),
      width: '170px',
    },
    {
      title: 'Source',
      key: 'source',
      render: (_, record: HistoryRow) => (
        <Tag color={getSourceColor(record.source)}>{record.source}</Tag>
      ),
      width: '120px',
    },
    {
      title: 'Changed By',
      dataIndex: 'changedByName',
      key: 'changedByName',
    },
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
    },
    {
      title: 'Before',
      key: 'before',
      render: (_, record: HistoryRow) => renderValue(record.before),
    },
    {
      title: 'After',
      key: 'after',
      render: (_, record: HistoryRow) => renderValue(record.after),
    },
  ];

  const title = `${salesOrderLine.nsSalesOrder?.tranId ?? salesOrderLine.id} Line ${salesOrderLine.nsSalesOrderLine?.line ?? ''}`;

  return (
    <Modal
      open
      title={<ModalTitle>{`Change History: ${title}`}</ModalTitle>}
      onCancel={onClose}
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      }
      width={1000}
    >
      <IBTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey={(record) => record.key}
        locale={{ emptyText: 'No changes recorded' }}
        pagination={{
          pageSize,
          showSizeChanger: true,
          onChange: (_page, size) => setPageSize(size),
        }}
      />
    </Modal>
  );
};

export default SalesOrderLineHistoryModal;
